import "@mantine/dropzone/styles.css";

import {
  ActionIcon, Alert, Anchor, Autocomplete, Badge, Box, Button, Card, Checkbox, Collapse, ColorInput, Divider, Drawer, Grid, Group,
  Menu, Modal, MultiSelect, NumberInput, Paper, PasswordInput, ScrollArea, SegmentedControl, Select, SimpleGrid, Skeleton, Slider,
  Stack, Switch, Table, Tabs, Text, Textarea, TextInput, ThemeIcon, Title, Tooltip,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconCheck, IconCircleCheck, IconCloudUpload, IconDots, IconEdit, IconEye, IconEyeOff, IconFile,
  IconInfoCircle, IconLock, IconMap, IconPlugConnected, IconPlus, IconRefresh, IconSearch, IconServer, IconStack2, IconTrash, IconUpload,
  IconWorld, IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import OlMap from "ol/Map";
import View from "ol/View";
import { defaults as defaultControls } from "ol/control/defaults";
import TileLayer from "ol/layer/Tile";
import { fromLonLat, transformExtent } from "ol/proj";
import { OSM } from "ol/source";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { http } from "../../api/http";
import { useCan, useUser } from "../../auth/user";
import { Empty, fmtDateTime, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import { createLayer, type LayerDef } from "../../map/layers";
import AdminLayout from "./AdminLayout";
import LayerVisibilityMatrix from "./LayerVisibilityMatrix";

// ============================================================================ tipovi
type Kind = "wms" | "wmts" | "wfs" | "xyz" | "arcgis";
interface Cap { name: string; title?: string | null; abstract?: string | null; group?: string | null; crs?: (string | null)[];
  bbox?: number[] | null; queryable?: boolean; styles?: string[]; matrix_sets?: string[]; formats?: string[] }
interface Service {
  id: number; name: string; kind: Kind; url: string; version: string | null; auth_type: "none" | "basic" | "header";
  username: string | null; header_name: string | null; has_secret: boolean; use_proxy: boolean; provider: string | null;
  description: string | null; enabled: boolean; last_check_at: string | null; last_check_ok: boolean | null; last_check_msg: string | null;
  capabilities: Cap[]; used: number; pending: number; layer_count: number;
}
interface CheckResult { ok: boolean; message: string; layers: Cap[] }
interface ManagedLayer {
  id: number; code: string; title: string; group_name: string; kind: string; url: string; options: Record<string, unknown>;
  min_zoom: number | null; max_zoom: number | null; visible_default: boolean; opacity: number; sort_order: number;
  required_permission: string; is_basemap: boolean; description: string | null; source_id: number | null; source_layer: string | null;
  dataset_id: number | null; origin: "sistem" | "geoservis" | "fajl"; status: "predlozen" | "odobren" | "odbijen" | "iskljucen";
  allowed_roles: string[] | null; proposed_by: string | null; approved_by: string | null; approved_at: string | null;
  review_note: string | null; attribution: string | null; service_name: string | null; service_kind: string | null;
  feature_count: number | null; geometry_type: string | null; filename: string | null;
}
interface Role { name: string; description?: string | null }
interface UploadResult { dataset_id: number; layer_id: number; features: number; fields: string[]; srs: string | null;
  geometry_type: string | null; bbox: number[] | null; status: string }

const KIND_INFO: Record<Kind, { label: string; color: string; text: string; example: string }> = {
  wms: { label: "WMS", color: "blue", text: "Slike karte koje server crta (katastar, ortofoto, tematske karte). Najčešći tip geoportala.",
    example: "https://geoportal.rgurs.org/geoserver/ows" },
  wmts: { label: "WMTS", color: "cyan", text: "Unaprijed izrezane pločice — brz prikaz podloga i ortofota.",
    example: "https://servis.primjer.org/geoserver/gwc/service/wmts" },
  wfs: { label: "WFS", color: "grape", text: "Vektorski objekti sa atributima (parcele, vodotoci) — mogu se kliknuti i pretraživati.",
    example: "https://servis.primjer.org/geoserver/ows" },
  xyz: { label: "XYZ", color: "teal", text: "Pločice po šablonu {z}/{x}/{y} (OpenStreetMap, satelitske podloge).",
    example: "https://tile.openstreetmap.org/{z}/{x}/{y}.png" },
  arcgis: { label: "ArcGIS", color: "orange", text: "ArcGIS REST MapServer pločice (Esri podloge).",
    example: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" },
};
const STATUS_META: Record<string, { color: string; label: string }> = {
  predlozen: { color: "orange", label: "predložen" }, odobren: { color: "green", label: "odobren" },
  odbijen: { color: "red", label: "odbijen" }, iskljucen: { color: "gray", label: "isključen" },
};
const ORIGIN_META: Record<string, { color: string; label: string }> = {
  sistem: { color: "dark", label: "sistem" }, geoservis: { color: "blue", label: "geoservis" }, fajl: { color: "grape", label: "fajl" },
};
const SRS_OPTIONS = [
  { value: "EPSG:4326", label: "EPSG:4326 — WGS 84 (GPS, geografske koordinate)" },
  { value: "EPSG:8678", label: "EPSG:8678 — MGI 1901 / Balkans zone 6 (RS, novi)" },
  { value: "EPSG:3908", label: "EPSG:3908 — MGI / Balkans zone 6 (Gauss-Krüger)" },
  { value: "EPSG:32634", label: "EPSG:32634 — WGS 84 / UTM 34N" },
  { value: "EPSG:3857", label: "EPSG:3857 — Web Mercator" },
];
const EXTS = [".zip", ".kml", ".kmz", ".geojson", ".json", ".gpkg", ".gml", ".csv", ".fgb", ".dxf", ".gpx"];
const VECTOR_KINDS = new Set(["wfs", "geojson"]);

// ============================================================================ pomoćno
function useRoles() {
  const can = useCan();
  return useQuery({ queryKey: ["layer-roles"], enabled: can("layers:propose"), staleTime: 300_000, retry: false,
    queryFn: () => http.get<Role[]>("/api/v1/geoservices/roles") });
}

function RolesSelect({ value, onChange, label = "Vidljivo rolama", size }: { value: string[]; onChange: (v: string[]) => void; label?: string; size?: "xs" | "sm" }) {
  const { t } = useT();
  const roles = useRoles();
  const data = useMemo(() => {
    const d = (roles.data ?? []).map((r) => ({ value: r.name, label: r.description ? `${r.description}` : r.name }));
    value.filter((v) => !d.some((x) => x.value === v)).forEach((v) => d.push({ value: v, label: v }));
    return d;
  }, [roles.data, value]);
  return (
    <MultiSelect label={t(label)} size={size} data={data} value={value} onChange={onChange} searchable clearable
      placeholder={value.length ? undefined : t("sve role (prazno = svi korisnici sa pristupom karti)")}
      description={!roles.data && !roles.isLoading ? t("Spisak rola nije dostupan vašem nalogu.") : undefined} />
  );
}

function RoleBadges({ roles }: { roles: string[] | null }) {
  const { t } = useT();
  if (!roles?.length) return <Badge size="sm" variant="light" color="green" leftSection={<IconWorld size={11} />}>{t("sve role")}</Badge>;
  return <Group gap={4}>{roles.map((r) => <Badge key={r} size="sm" variant="light" color="gray" tt="none">{r}</Badge>)}</Group>;
}

const errNote = (t: (s: string) => string) => (e: Error) => notifications.show({ color: "red", title: t("Greška"), message: e.message });

/** LayerDef za pregled — spoljni servisi uvijek kroz serverski proksi (kredencijali i token ne idu trećoj strani). */
function previewDef(l: ManagedLayer): LayerDef {
  let url = l.url;
  if (l.origin === "geoservis" && l.source_id) {
    url = l.kind === "xyz" ? `/api/v1/geoservices/${l.source_id}/tile/{z}/{x}/{y}` : `/api/v1/geoservices/${l.source_id}/ows`;
  }
  return { id: l.id, code: l.code, title: l.title, group_name: l.group_name, kind: l.kind, url, geoserver_layer: null, style: null,
    options: l.options ?? {}, min_zoom: l.kind === "wfs" ? 0 : null, max_zoom: null, visible_default: true, opacity: l.opacity ?? 1,
    sort_order: 10, is_basemap: false, description: l.description, attribution: l.attribution };
}

function validBbox(b: unknown): b is number[] {
  return Array.isArray(b) && b.length === 4 && b.every((x) => typeof x === "number" && Number.isFinite(x)) && b[0] < b[2] && b[1] < b[3];
}

function PreviewMap({ def, bbox, height = 220 }: { def: LayerDef | null; bbox?: number[] | null; height?: number }) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const key = def ? `${def.kind}|${def.url}|${JSON.stringify(def.options)}|${def.opacity}` : "";
  useEffect(() => {
    if (!ref.current) return;
    const layers = [new TileLayer({ source: new OSM() })];
    const overlay = def ? createLayer(def) : null;
    const map = new OlMap({
      target: ref.current, controls: defaultControls({ rotate: false, attributionOptions: { collapsible: true } }),
      layers: overlay ? [...layers, overlay] : layers,
      view: new View({ center: fromLonLat([17.6, 44.65]), zoom: 7, maxZoom: 19 }),
    });
    const b = bbox ?? (def?.options as { bbox?: unknown } | undefined)?.bbox;
    if (validBbox(b)) {
      // preveliki obuhvat (npr. cijeli svijet) — ostaje pogled na RS
      if (b[2] - b[0] < 20) map.getView().fit(transformExtent(b, "EPSG:4326", "EPSG:3857"), { padding: [16, 16, 16, 16], maxZoom: 16 });
    }
    return () => map.setTarget(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, JSON.stringify(bbox)]);
  return (
    <Box pos="relative" style={{ height, borderRadius: 12, overflow: "hidden", border: "1px solid var(--mantine-color-default-border)" }}>
      <div ref={ref} style={{ position: "absolute", inset: 0 }} />
      {def?.kind === "wmts" && <Badge pos="absolute" top={8} left={8} size="xs" variant="white">{t("WMTS pregled")}</Badge>}
    </Box>
  );
}

function SourceLine({ l }: { l: ManagedLayer }) {
  const { t } = useT();
  if (l.origin === "fajl") return (
    <Group gap={6}><IconFile size={14} opacity={0.6} /><Text size="sm">{l.filename ?? t("fajl")}</Text>
      {l.feature_count != null && <Badge size="xs" variant="light">{l.feature_count.toLocaleString("sr-Latn-BA")} {t("objekata")}</Badge>}
      {l.geometry_type && <Badge size="xs" variant="outline" color="gray" tt="none">{l.geometry_type}</Badge>}</Group>
  );
  if (l.origin === "geoservis") return (
    <Group gap={6}><IconServer size={14} opacity={0.6} /><Text size="sm">{l.service_name ?? "—"}</Text>
      <Badge size="xs" variant="light" color={KIND_INFO[(l.service_kind ?? l.kind) as Kind]?.color ?? "gray"}>{(l.service_kind ?? l.kind).toUpperCase()}</Badge>
      {l.source_layer && <Text size="xs" c="dimmed" ff="monospace">{l.source_layer}</Text>}</Group>
  );
  return <Group gap={6}><IconStack2 size={14} opacity={0.6} /><Text size="sm">{t("Sistemski sloj platforme")}</Text></Group>;
}

// ============================================================================ stranica
export default function GeoservicesPage() {
  const { t } = useT();
  const can = useCan();
  const manager = can("layers:manage");
  const [sp, setSp] = useSearchParams();
  const layers = useQuery({ queryKey: ["layers-manage", "all"], queryFn: () => http.get<ManagedLayer[]>("/api/v1/layers/manage") });
  const services = useQuery({ queryKey: ["geoservices"], queryFn: () => http.get<Service[]>("/api/v1/geoservices") });
  const pending = (layers.data ?? []).filter((l) => l.status === "predlozen");
  const tab = sp.get("tab") ?? (sp.get("status") === "predlozen" ? "odobrenje" : "odobrenje");
  const setTab = (v: string | null) => setSp((p) => { const n = new URLSearchParams(p); n.delete("status"); if (v) n.set("tab", v); return n; }, { replace: true });
  const groups = useMemo(() => [...new Set((layers.data ?? []).map((l) => l.group_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, "sr")), [layers.data]);

  return (
    <AdminLayout>
      <PageHeader title="Geoservisi i slojevi" description="Povežite spoljne geoservise (WMS, WMTS, WFS, XYZ), uvezite GIS fajlove i odlučite ko vidi koji sloj na karti." />
      <Alert icon={<IconLock size={18} />} color="agri" variant="light" mb="md" radius="lg">
        {t("Svaki novi sloj je vidljiv tek kad ga administrator odobri i odredi kojim rolama je dostupan. Kredencijali geoservisa ostaju na serveru.")}
      </Alert>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="odobrenje" leftSection={<IconCircleCheck size={16} />}
            rightSection={pending.length ? <Badge size="xs" color="orange" circle>{pending.length}</Badge> : undefined}>{t("Na odobrenju")}</Tabs.Tab>
          {manager && <Tabs.Tab value="vidljivost" leftSection={<IconEye size={16} />}>{t("Ko vidi koji sloj")}</Tabs.Tab>}
          <Tabs.Tab value="slojevi" leftSection={<IconStack2 size={16} />}>{t("Slojevi")}</Tabs.Tab>
          <Tabs.Tab value="servisi" leftSection={<IconServer size={16} />}>{t("Geoservisi")}</Tabs.Tab>
          <Tabs.Tab value="uvoz" leftSection={<IconUpload size={16} />}>{t("Uvoz fajla")}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="odobrenje">
          {layers.isLoading ? <Skeleton h={260} radius="lg" /> : <PendingTab layers={layers.data ?? []} services={services.data ?? []} groups={groups} manager={manager} />}
        </Tabs.Panel>
        <Tabs.Panel value="vidljivost"><LayerVisibilityMatrix /></Tabs.Panel>
        <Tabs.Panel value="slojevi">
          {layers.isLoading ? <Skeleton h={400} radius="lg" /> : <LayersTab layers={layers.data ?? []} groups={groups} manager={manager} />}
        </Tabs.Panel>
        <Tabs.Panel value="servisi">
          <ServicesTab services={services.data ?? []} loading={services.isLoading} layers={layers.data ?? []} groups={groups} manager={manager} />
        </Tabs.Panel>
        <Tabs.Panel value="uvoz">
          <UploadTab groups={groups} manager={manager} onGoPending={() => setTab("odobrenje")} />
        </Tabs.Panel>
      </Tabs>
    </AdminLayout>
  );
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["layers-manage"] });
    qc.invalidateQueries({ queryKey: ["geoservices"] });
    qc.invalidateQueries({ queryKey: ["layers"] });
  };
}

// ============================================================================ Na odobrenju
function PendingTab({ layers, services, groups, manager }: { layers: ManagedLayer[]; services: Service[]; groups: string[]; manager: boolean }) {
  const { t } = useT();
  const pending = layers.filter((l) => l.status === "predlozen");
  const rejected = layers.filter((l) => l.status === "odbijen");
  const [showRej, { toggle }] = useDisclosure(false);
  const inv = useInvalidate();
  const del = useMutation({ mutationFn: (id: number) => http.del(`/api/v1/layers/${id}`), onSuccess: inv, onError: errNote(t) });
  return (
    <Stack>
      {!pending.length ? (
        <Paper withBorder radius="lg" p="xl">
          <Stack align="center" gap={6}>
            <ThemeIcon size={48} radius="xl" variant="light"><IconCheck /></ThemeIcon>
            <Text fw={650}>{t("Nema slojeva koji čekaju odobrenje")}</Text>
            <Text size="sm" c="dimmed" ta="center" maw={520}>{t("Novi slojevi dodati iz geoservisa ili uvezeni iz fajla pojaviće se ovdje dok ih administrator ne odobri.")}</Text>
          </Stack>
        </Paper>
      ) : pending.map((l) => <PendingCard key={l.id} l={l} services={services} groups={groups} manager={manager} />)}
      {rejected.length > 0 && (
        <Paper withBorder radius="lg" p="sm">
          <Group justify="space-between">
            <Text size="sm" fw={600}>{t("Odbijeni predlozi")} <Badge size="xs" color="red" variant="light">{rejected.length}</Badge></Text>
            <Button size="xs" variant="subtle" onClick={toggle}>{showRej ? t("Sakrij") : t("Prikaži")}</Button>
          </Group>
          <Collapse expanded={showRej}>
            <Table mt="xs" fz="sm" verticalSpacing={6}>
              <Table.Tbody>
                {rejected.map((l) => (
                  <Table.Tr key={l.id}>
                    <Table.Td><Text size="sm" fw={600}>{l.title}</Text><SourceLine l={l} /></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{t("predložio")} {l.proposed_by ?? "—"} · {t("odbio")} {l.approved_by ?? "—"}, {fmtDateTime(l.approved_at)}</Text>
                      {l.review_note && <Text size="xs">„{l.review_note}“</Text>}</Table.Td>
                    <Table.Td w={40}>{manager && <ActionIcon variant="subtle" color="red" onClick={() => window.confirm(t("Trajno obrisati odbijeni sloj?")) && del.mutate(l.id)}><IconTrash size={16} /></ActionIcon>}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Collapse>
        </Paper>
      )}
    </Stack>
  );
}

function PendingCard({ l, services, groups, manager }: { l: ManagedLayer; services: Service[]; groups: string[]; manager: boolean }) {
  const { t } = useT();
  const inv = useInvalidate();
  const [roles, setRoles] = useState<string[]>(l.allowed_roles ?? []);
  const [title, setTitle] = useState(l.title);
  const [group, setGroup] = useState(l.group_name);
  const [note, setNote] = useState("");
  const cap = l.source_id ? services.find((s) => s.id === l.source_id)?.capabilities?.find((c) => c.name === l.source_layer) : undefined;
  const approve = useMutation({
    mutationFn: () => http.post(`/api/v1/layers/${l.id}/approve`, { allowed_roles: roles, note: note || null, group_name: group || null, title: title || null }),
    onSuccess: () => { notifications.show({ color: "green", message: `${t("Sloj odobren")}: ${title}` }); inv(); }, onError: errNote(t),
  });
  const reject = useMutation({
    mutationFn: () => http.post(`/api/v1/layers/${l.id}/reject`, { note: note || null }),
    onSuccess: () => { notifications.show({ color: "orange", message: `${t("Sloj odbijen")}: ${title}` }); inv(); }, onError: errNote(t),
  });
  return (
    <Card withBorder radius="lg" padding="md">
      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <PreviewMap def={previewDef(l)} bbox={cap?.bbox ?? null} height={240} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Stack gap="xs">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <div style={{ minWidth: 0 }}>
                <Text fw={700} size="lg" lineClamp={2}>{l.title}</Text>
                <SourceLine l={l} />
              </div>
              <Badge color="orange" variant="light" style={{ flexShrink: 0 }}>{t("čeka odobrenje")}</Badge>
            </Group>
            <Text size="xs" c="dimmed">{t("Predložio")}: <b>{l.proposed_by ?? "—"}</b> · {t("grupa")}: {l.group_name}</Text>
            {l.description && <Text size="sm" c="dimmed" lineClamp={3}>{l.description}</Text>}
            {manager ? (
              <>
                <Divider my={4} />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                  <TextInput size="sm" label={t("Naziv na karti")} value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
                  <Autocomplete size="sm" label={t("Grupa u legendi")} data={groups} value={group} onChange={setGroup} />
                </SimpleGrid>
                <RolesSelect value={roles} onChange={setRoles} size="sm" />
                <Textarea size="sm" label={t("Napomena predlagaču")} placeholder={t("npr. razlog odbijanja ili uputa")} autosize minRows={1}
                  value={note} onChange={(e) => setNote(e.currentTarget.value)} />
                <Group justify="flex-end" gap="xs" mt={4}>
                  <Button variant="light" color="red" leftSection={<IconX size={16} />} loading={reject.isPending}
                    onClick={() => reject.mutate()}>{t("Odbij")}</Button>
                  <Button leftSection={<IconCheck size={16} />} loading={approve.isPending} onClick={() => approve.mutate()}>
                    {t("Odobri")}{roles.length ? ` (${roles.length} ${t("rola")})` : ` — ${t("sve role")}`}
                  </Button>
                </Group>
              </>
            ) : (
              <Alert color="orange" variant="light" p="xs" icon={<IconInfoCircle size={16} />}>
                <Text size="sm">{t("Čeka odluku administratora slojeva — tek tada postaje vidljiv na karti.")}</Text>
              </Alert>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Card>
  );
}

// ============================================================================ Slojevi
function LayersTab({ layers, groups, manager }: { layers: ManagedLayer[]; groups: string[]; manager: boolean }) {
  const { t } = useT();
  const inv = useInvalidate();
  const [q, setQ] = useState("");
  const [origin, setOrigin] = useState("sve");
  const [edit, setEdit] = useState<ManagedLayer | null>(null);
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => http.post(`/api/v1/layers/${id}/disable`, undefined, { enabled }),
    onSuccess: inv, onError: errNote(t),
  });
  const del = useMutation({
    mutationFn: (id: number) => http.del(`/api/v1/layers/${id}`),
    onSuccess: () => { notifications.show({ color: "green", message: t("Sloj obrisan") }); inv(); }, onError: errNote(t),
  });
  const shown = layers.filter((l) => (l.status === "odobren" || l.status === "iskljucen") && (origin === "sve" || l.origin === origin)
    && (!q || `${l.title} ${l.code} ${l.service_name ?? ""} ${l.filename ?? ""}`.toLowerCase().includes(q.toLowerCase())));
  const byGroup = useMemo(() => {
    const m = new Map<string, ManagedLayer[]>();
    shown.forEach((l) => { const g = l.group_name || t("Bez grupe"); m.set(g, [...(m.get(g) ?? []), l]); });
    return [...m.entries()];
  }, [shown, t]);

  return (
    <Stack>
      <Group justify="space-between" wrap="wrap">
        <TextInput placeholder={t("Pretraga slojeva")} leftSection={<IconSearch size={15} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} w={280} />
        <SegmentedControl size="xs" value={origin} onChange={setOrigin} data={[
          { value: "sve", label: t("Svi") }, { value: "sistem", label: t("Sistemski") }, { value: "geoservis", label: t("Geoservisi") }, { value: "fajl", label: t("Fajlovi") },
        ]} />
      </Group>
      {!byGroup.length && <Empty text="Nema slojeva za prikaz" />}
      {byGroup.map(([g, rows]) => (
        <Paper key={g} withBorder radius="lg" style={{ overflow: "hidden" }}>
          <Group px="md" py={8} bg="var(--mantine-color-default-hover)" justify="space-between">
            <Text fw={700} size="sm">{g}</Text><Badge size="sm" variant="light" color="gray">{rows.length}</Badge>
          </Group>
          <ScrollArea>
            <Table verticalSpacing={8} fz="sm" highlightOnHover miw={900} layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w="30%">{t("Sloj")}</Table.Th><Table.Th w={70}>{t("Vrsta")}</Table.Th><Table.Th w={140}>{t("Izvor")}</Table.Th>
                  <Table.Th w={170}>{t("Vidljivo rolama")}</Table.Th><Table.Th w={90} ta="center">{t("Providnost")}</Table.Th>
                  <Table.Th w={80} ta="center">{t("Na startu")}</Table.Th><Table.Th w={70} ta="center">{t("Aktivan")}</Table.Th><Table.Th w={50} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((l) => (
                  <Table.Tr key={l.id} style={{ opacity: l.status === "iskljucen" ? 0.55 : 1 }}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        {VECTOR_KINDS.has(l.kind) && <Box w={12} h={12} style={{ flexShrink: 0, borderRadius: 3, border: `2px solid ${String(l.options?.stroke ?? "#2563eb")}`, background: String(l.options?.fill ?? "transparent") }} />}
                        <Text size="sm" fw={600} lineClamp={1}>{l.title}</Text>
                        {l.is_basemap && <Badge size="xs" variant="outline" color="gray">{t("podloga")}</Badge>}
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={1}>{l.description ?? l.code}</Text>
                    </Table.Td>
                    <Table.Td><Badge size="sm" variant="light" color={KIND_INFO[l.kind as Kind]?.color ?? (l.kind === "mvt" ? "agri" : "gray")}>{l.kind.toUpperCase()}</Badge></Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light" color={ORIGIN_META[l.origin]?.color}>{t(ORIGIN_META[l.origin]?.label ?? l.origin)}</Badge>
                      <Text size="xs" c="dimmed" lineClamp={1}>{l.service_name ?? l.filename ?? ""}</Text>
                    </Table.Td>
                    <Table.Td><RoleBadges roles={l.allowed_roles} /></Table.Td>
                    <Table.Td ta="center">{Math.round((l.opacity ?? 1) * 100)}%</Table.Td>
                    <Table.Td ta="center">{l.visible_default ? <IconEye size={16} color="var(--mantine-color-agri-6)" /> : <IconEyeOff size={16} opacity={0.4} />}</Table.Td>
                    <Table.Td ta="center">
                      <Tooltip label={l.status === "odobren" ? t("Isključi sloj (nestaje sa karte za sve)") : t("Ponovo uključi sloj")}>
                        <Switch mx="auto" w="fit-content" size="sm" checked={l.status === "odobren"} disabled={!manager}
                          onChange={(e) => toggle.mutate({ id: l.id, enabled: e.currentTarget.checked })} />
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      {manager && (
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target><ActionIcon variant="subtle" color="gray"><IconDots size={16} /></ActionIcon></Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => setEdit(l)}>{t("Izmijeni")}</Menu.Item>
                            {l.origin !== "sistem" && (
                              <Menu.Item color="red" leftSection={<IconTrash size={15} />}
                                onClick={() => window.confirm(`${t("Trajno obrisati sloj")} „${l.title}“?`) && del.mutate(l.id)}>{t("Obriši")}</Menu.Item>
                            )}
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      ))}
      {edit && <LayerEditDrawer layer={edit} groups={groups} onClose={() => setEdit(null)} />}
    </Stack>
  );
}

function LayerEditDrawer({ layer: l, groups, onClose }: { layer: ManagedLayer; groups: string[]; onClose: () => void }) {
  const { t } = useT();
  const inv = useInvalidate();
  const o = l.options ?? {};
  const [v, setV] = useState({
    title: l.title, group_name: l.group_name, description: l.description ?? "", allowed_roles: l.allowed_roles ?? [], opacity: l.opacity ?? 1,
    visible_default: l.visible_default, is_basemap: l.is_basemap, min_zoom: l.min_zoom, max_zoom: l.max_zoom, sort_order: l.sort_order,
    stroke: String(o.stroke ?? "#2563eb"), fill: String(o.fill ?? "rgba(37,99,235,0.08)"), label: String(o.label ?? ""),
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((s) => ({ ...s, [k]: val }));
  const vector = VECTOR_KINDS.has(l.kind);
  const save = useMutation({
    mutationFn: () => http.put(`/api/v1/layers/${l.id}`, {
      title: v.title, group_name: v.group_name, description: v.description, allowed_roles: v.allowed_roles, opacity: v.opacity,
      visible_default: v.visible_default, is_basemap: v.is_basemap, min_zoom: v.min_zoom, max_zoom: v.max_zoom, sort_order: v.sort_order,
      ...(vector ? { style: { stroke: v.stroke, fill: v.fill, label: v.label || null } } : {}),
    }),
    onSuccess: () => { notifications.show({ color: "green", message: t("Sloj sačuvan") }); inv(); onClose(); }, onError: errNote(t),
  });
  const def = { ...previewDef(l), opacity: v.opacity, options: vector ? { ...o, stroke: v.stroke, fill: v.fill, label: v.label || undefined } : o };
  return (
    <Drawer opened onClose={onClose} position="right" size={500} title={<Text fw={700}>{t("Izmjena sloja")}</Text>}>
      <Stack gap="sm">
        <PreviewMap def={def} height={180} />
        <Group gap={6}><Badge variant="light" color={ORIGIN_META[l.origin]?.color}>{t(ORIGIN_META[l.origin]?.label ?? l.origin)}</Badge>
          <Badge variant="light" color="gray">{l.kind.toUpperCase()}</Badge><Text size="xs" c="dimmed" ff="monospace">{l.code}</Text></Group>
        <TextInput label={t("Naziv na karti")} value={v.title} onChange={(e) => set("title", e.currentTarget.value)} />
        <Autocomplete label={t("Grupa u legendi")} data={groups} value={v.group_name} onChange={(x) => set("group_name", x)} />
        <Textarea label={t("Opis")} autosize minRows={2} value={v.description} onChange={(e) => set("description", e.currentTarget.value)} />
        <RolesSelect value={v.allowed_roles} onChange={(x) => set("allowed_roles", x)} />
        <div>
          <Text size="sm" fw={500}>{t("Providnost")} <Text span c="dimmed" size="sm">{Math.round(v.opacity * 100)}%</Text></Text>
          <Slider min={0} max={1} step={0.05} value={v.opacity} onChange={(x) => set("opacity", x)} label={(x) => `${Math.round(x * 100)}%`} mt={4} />
        </div>
        <Switch label={t("Uključen kada se karta otvori")} checked={v.visible_default} onChange={(e) => set("visible_default", e.currentTarget.checked)} />
        <Switch label={t("Podloga (osnovna karta, bira se jedna)")} checked={v.is_basemap} onChange={(e) => set("is_basemap", e.currentTarget.checked)} />
        <SimpleGrid cols={3} spacing="xs">
          <NumberInput label={t("Od zuma")} min={0} max={22} value={v.min_zoom ?? ""} onChange={(x) => set("min_zoom", x === "" ? null : Number(x))} />
          <NumberInput label={t("Do zuma")} min={0} max={22} value={v.max_zoom ?? ""} onChange={(x) => set("max_zoom", x === "" ? null : Number(x))} />
          <NumberInput label={t("Redoslijed")} value={v.sort_order} onChange={(x) => set("sort_order", Number(x) || 0)} />
        </SimpleGrid>
        {vector && (
          <Paper withBorder radius="md" p="sm">
            <Text size="sm" fw={600} mb={6}>{t("Izgled objekata")}</Text>
            <SimpleGrid cols={2} spacing="xs">
              <ColorInput label={t("Boja linije")} format="hex" value={v.stroke} onChange={(x) => set("stroke", x)} />
              <ColorInput label={t("Boja ispune")} format="rgba" value={v.fill} onChange={(x) => set("fill", x)} />
            </SimpleGrid>
            <TextInput mt="xs" label={t("Natpis (naziv atributa)")} placeholder="npr. BROJ" value={v.label} onChange={(e) => set("label", e.currentTarget.value)} />
          </Paper>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>{t("Odustani")}</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>{t("Sačuvaj")}</Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

// ============================================================================ Geoservisi
function StatusDot({ s }: { s: Service }) {
  const { t } = useT();
  const color = !s.enabled ? "gray" : s.last_check_ok == null ? "yellow" : s.last_check_ok ? "green" : "red";
  const label = !s.enabled ? t("isključen") : s.last_check_ok == null ? t("nije provjeren") : s.last_check_ok ? t("radi") : t("ne radi");
  return (
    <Tooltip label={s.last_check_msg ?? label} multiline w={280} withArrow>
      <Group gap={6} wrap="nowrap">
        <Box w={10} h={10} style={{ borderRadius: 99, background: `var(--mantine-color-${color}-6)`, boxShadow: `0 0 0 3px var(--mantine-color-${color}-1)` }} />
        <Text size="xs" c="dimmed">{label}</Text>
      </Group>
    </Tooltip>
  );
}

function ServicesTab({ services, loading, layers, groups, manager }: { services: Service[]; loading: boolean; layers: ManagedLayer[]; groups: string[]; manager: boolean }) {
  const { t } = useT();
  const inv = useInvalidate();
  const [wizard, setWizard] = useState<Service | "new" | null>(null);
  const [pick, setPick] = useState<number | null>(null);
  const [checking, setChecking] = useState<number | null>(null);
  const check = useMutation({
    mutationFn: (id: number) => { setChecking(id); return http.post<CheckResult>(`/api/v1/geoservices/${id}/check`); },
    onSuccess: (r) => { notifications.show({ color: r.ok ? "green" : "red", title: r.ok ? t("Veza radi") : t("Veza ne radi"), message: r.message }); inv(); },
    onError: errNote(t), onSettled: () => setChecking(null),
  });
  const del = useMutation({
    mutationFn: (id: number) => http.del(`/api/v1/geoservices/${id}`),
    onSuccess: () => { notifications.show({ color: "green", message: t("Konekcija obrisana") }); inv(); }, onError: errNote(t),
  });
  const pickSvc = services.find((s) => s.id === pick) ?? null;
  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{t("Konekcije na spoljne servise. Zahtjevi idu preko servera, pa korisnici nikad ne vide lozinke.")}</Text>
        {manager && <Button leftSection={<IconPlus size={16} />} onClick={() => setWizard("new")}>{t("Nova konekcija")}</Button>}
      </Group>
      {loading ? <Skeleton h={200} radius="lg" /> : !services.length ? <Empty text="Još nema konekcija na geoservise" /> : (
        <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="md">
          {services.map((s) => (
            <Card key={s.id} withBorder radius="lg" padding="md" style={{ opacity: s.enabled ? 1 : 0.7, display: "flex", flexDirection: "column" }}>
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" radius="md" size={38} color={KIND_INFO[s.kind]?.color}><IconPlugConnected size={20} /></ThemeIcon>
                  <div style={{ minWidth: 0 }}>
                    <Text fw={700} lineClamp={2} lh={1.25}>{s.name}</Text>
                    {s.provider && <Text size="xs" c="dimmed" lineClamp={1}>{s.provider}</Text>}
                  </div>
                </Group>
                <Badge variant="light" color={KIND_INFO[s.kind]?.color} style={{ flexShrink: 0 }}>{KIND_INFO[s.kind]?.label ?? s.kind}</Badge>
              </Group>
              <Text size="xs" ff="monospace" c="dimmed" mt="xs" lineClamp={1} title={s.url}>{s.url}</Text>
              <Group gap={6} mt="xs">
                <StatusDot s={s} />
                {s.auth_type !== "none" && <Badge size="xs" variant="light" color={s.has_secret ? "blue" : "orange"} leftSection={<IconLock size={10} />}>
                  {t("prijava")}{s.has_secret ? "" : ` — ${t("nedostaje lozinka")}`}</Badge>}
                {!s.enabled && <Badge size="xs" color="gray">{t("isključen")}</Badge>}
              </Group>
              {s.last_check_msg && <Text size="xs" c={s.last_check_ok ? "dimmed" : "red"} mt={6} lineClamp={2}>{s.last_check_msg}{s.last_check_at ? ` · ${fmtDateTime(s.last_check_at)}` : ""}</Text>}
              {s.description && <Text size="xs" c="dimmed" mt={6} lineClamp={2}>{s.description}</Text>}
              <Group gap="lg" mt="sm">
                <div><Text fw={700}>{s.layer_count}</Text><Text size="xs" c="dimmed">{t("dostupno")}</Text></div>
                <div><Text fw={700}>{s.used}</Text><Text size="xs" c="dimmed">{t("dodato")}</Text></div>
                <div><Text fw={700} c={s.pending ? "orange" : undefined}>{s.pending}</Text><Text size="xs" c="dimmed">{t("na odobrenju")}</Text></div>
              </Group>
              <Box style={{ flex: 1 }} />
              <Group gap={6} mt="md">
                <Button size="xs" leftSection={<IconStack2 size={14} />} onClick={() => setPick(s.id)}>{t("Slojevi")}</Button>
                {manager && <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} loading={checking === s.id}
                  onClick={() => check.mutate(s.id)}>{t("Provjeri")}</Button>}
                {manager && <Button size="xs" variant="default" leftSection={<IconEdit size={14} />} onClick={() => setWizard(s)}>{t("Izmijeni")}</Button>}
                {manager && <ActionIcon variant="subtle" color="red" ml="auto" aria-label={t("Obriši")}
                  onClick={() => window.confirm(`${t("Obrisati konekciju")} „${s.name}“${s.used ? ` ${t("i njenih")} ${s.used} ${t("slojeva")}` : ""}?`) && del.mutate(s.id)}><IconTrash size={16} /></ActionIcon>}
              </Group>
            </Card>
          ))}
        </SimpleGrid>
      )}
      {wizard && <ServiceWizard initial={wizard === "new" ? null : wizard} onClose={() => setWizard(null)}
        onPickLayers={(id) => { setWizard(null); setPick(id); }} />}
      {pickSvc && <CapabilitiesDrawer service={pickSvc} layers={layers} groups={groups} manager={manager} onClose={() => setPick(null)} />}
    </Stack>
  );
}

function ServiceWizard({ initial, onClose, onPickLayers }: { initial: Service | null; onClose: () => void; onPickLayers: (id: number) => void }) {
  const { t } = useT();
  const inv = useInvalidate();
  const [v, setV] = useState({
    name: initial?.name ?? "", kind: (initial?.kind ?? "wms") as Kind, url: initial?.url ?? "", version: initial?.version ?? "",
    auth_type: initial?.auth_type ?? "none", username: initial?.username ?? "", password: "", header_name: initial?.header_name ?? "",
    use_proxy: initial?.use_proxy ?? true, provider: initial?.provider ?? "", description: initial?.description ?? "", enabled: initial?.enabled ?? true,
  });
  const [adv, { toggle: toggleAdv }] = useDisclosure(false);
  const [result, setResult] = useState<(CheckResult & { id: number }) | null>(null);
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => { setV((s) => ({ ...s, [k]: val })); setResult(null); };
  const info = KIND_INFO[v.kind];
  const body = () => ({
    name: v.name.trim(), kind: v.kind, url: v.url.trim(), version: v.version || null, auth_type: v.auth_type,
    username: v.auth_type === "basic" ? v.username || null : null, header_name: v.auth_type === "header" ? v.header_name || null : null,
    // izmjena: null = lozinka bez promjene; nova konekcija: prazno = bez lozinke
    password: v.auth_type === "none" ? (initial?.has_secret ? "" : null) : (v.password ? v.password : null),
    use_proxy: v.use_proxy, provider: v.provider || null, description: v.description || null, enabled: v.enabled,
  });
  const submit = useMutation({
    mutationFn: async () => {
      if (initial) {
        await http.put(`/api/v1/geoservices/${initial.id}`, body());
        const r = await http.post<CheckResult>(`/api/v1/geoservices/${initial.id}/check`);
        return { ...r, id: initial.id };
      }
      const r = await http.post<Service & { check: CheckResult }>("/api/v1/geoservices", body());
      return { ...r.check, id: r.id };
    },
    onSuccess: (r) => { setResult(r); inv(); }, onError: errNote(t),
  });
  const valid = v.name.trim().length >= 2 && /^https?:\/\/.{4,}/.test(v.url.trim());
  return (
    <Modal opened onClose={onClose} size="lg" radius="lg" title={<Text fw={700}>{initial ? t("Izmjena konekcije") : t("Nova konekcija na geoservis")}</Text>}>
      <Stack gap="sm">
        <div>
          <Text size="sm" fw={500} mb={4}>{t("Vrsta servisa")}</Text>
          <SegmentedControl fullWidth value={v.kind} onChange={(x) => set("kind", x as Kind)}
            data={(Object.keys(KIND_INFO) as Kind[]).map((k) => ({ value: k, label: KIND_INFO[k].label }))} />
          <Text size="xs" c="dimmed" mt={6}>{t(info.text)}</Text>
        </div>
        <TextInput label={t("Adresa servisa (URL)")} required value={v.url} onChange={(e) => set("url", e.currentTarget.value)}
          placeholder={info.example} leftSection={<IconWorld size={15} />}
          description={<>{t("Primjer")}: <Anchor size="xs" ff="monospace" onClick={() => set("url", info.example)}>{info.example}</Anchor></>} />
        <TextInput label={t("Naziv konekcije")} required placeholder={t("npr. RGURS Geoportal — katastar")} value={v.name} onChange={(e) => set("name", e.currentTarget.value)} />
        <TextInput label={t("Izvor / vlasnik podataka")} placeholder={t("prikazuje se kao atribucija na karti")} value={v.provider} onChange={(e) => set("provider", e.currentTarget.value)} />
        <div>
          <Text size="sm" fw={500} mb={4}>{t("Prijava na servis")}</Text>
          <SegmentedControl value={v.auth_type} onChange={(x) => set("auth_type", x as typeof v.auth_type)} data={[
            { value: "none", label: t("Javan") }, { value: "basic", label: t("Korisničko ime i lozinka") }, { value: "header", label: t("API ključ (zaglavlje)") },
          ]} />
        </div>
        {v.auth_type === "basic" && (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <TextInput label={t("Korisničko ime")} value={v.username} onChange={(e) => set("username", e.currentTarget.value)} autoComplete="off" />
            <PasswordInput label={t("Lozinka")} value={v.password} onChange={(e) => set("password", e.currentTarget.value)} autoComplete="new-password"
              placeholder={initial?.has_secret ? t("•••••• (ostavite prazno za bez promjene)") : undefined} />
          </SimpleGrid>
        )}
        {v.auth_type === "header" && (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <TextInput label={t("Naziv zaglavlja")} placeholder="X-API-Key" value={v.header_name} onChange={(e) => set("header_name", e.currentTarget.value)} />
            <PasswordInput label={t("Vrijednost ključa")} value={v.password} onChange={(e) => set("password", e.currentTarget.value)} autoComplete="new-password"
              placeholder={initial?.has_secret ? t("•••••• (ostavite prazno za bez promjene)") : undefined} />
          </SimpleGrid>
        )}
        {v.auth_type !== "none" && <Text size="xs" c="dimmed"><IconLock size={11} /> {t("Kredencijali se čuvaju šifrovano na serveru; karta ih nikad ne dobija.")}</Text>}
        <Anchor size="sm" onClick={toggleAdv}>{adv ? "▾" : "▸"} {t("Napredno")}</Anchor>
        <Collapse expanded={adv}>
          <Stack gap="xs">
            {["wms", "wfs", "wmts"].includes(v.kind) && <TextInput label={t("Verzija protokola")} placeholder={v.kind === "wfs" ? "2.0.0" : v.kind === "wms" ? "1.3.0" : "1.0.0"}
              description={t("Prazno = podrazumijevana.")} value={v.version} onChange={(e) => set("version", e.currentTarget.value)} maw={200} />}
            <Textarea label={t("Opis")} autosize minRows={2} value={v.description} onChange={(e) => set("description", e.currentTarget.value)} />
            <Switch label={t("Zahtjevi preko servera (proksi)")} description={t("Obavezno kada servis traži prijavu ili ne dozvoljava pristup iz pregledača.")}
              checked={v.use_proxy || v.auth_type !== "none"} disabled={v.auth_type !== "none"} onChange={(e) => set("use_proxy", e.currentTarget.checked)} />
            <Switch label={t("Konekcija aktivna")} checked={v.enabled} onChange={(e) => set("enabled", e.currentTarget.checked)} />
          </Stack>
        </Collapse>

        {result && (
          <Alert color={result.ok ? "green" : "red"} radius="md" icon={result.ok ? <IconCircleCheck /> : <IconAlertTriangle />}
            title={result.ok ? t("Veza radi — konekcija sačuvana") : t("Konekcija sačuvana, ali provjera nije uspjela")}>
            <Text size="sm">{result.message}</Text>
            {result.layers.length > 0 && (
              <>
                <ScrollArea.Autosize mah={160} mt="xs">
                  <Group gap={4}>{result.layers.slice(0, 40).map((c) => <Badge key={c.name} size="sm" variant="white" tt="none">{c.title ?? c.name}</Badge>)}
                    {result.layers.length > 40 && <Text size="xs">+{result.layers.length - 40}</Text>}</Group>
                </ScrollArea.Autosize>
                <Button mt="sm" size="xs" leftSection={<IconStack2 size={14} />} onClick={() => onPickLayers(result.id)}>{t("Izaberi slojeve za kartu")}</Button>
              </>
            )}
          </Alert>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>{result ? t("Zatvori") : t("Odustani")}</Button>
          <Button leftSection={<IconPlugConnected size={16} />} disabled={!valid} loading={submit.isPending} onClick={() => submit.mutate()}>
            {result && !result.ok ? t("Ponovo provjeri") : t("Provjeri i sačuvaj")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CapabilitiesDrawer({ service: s, layers, groups, manager, onClose }: { service: Service; layers: ManagedLayer[]; groups: string[]; manager: boolean; onClose: () => void }) {
  const { t } = useT();
  const inv = useInvalidate();
  const user = useUser();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [group, setGroup] = useState("Spoljni servisi");
  const [roles, setRoles] = useState<string[]>([]);
  const [basemap, setBasemap] = useState(false);
  const caps = s.capabilities ?? [];
  const existing = new Map(layers.filter((l) => l.source_id === s.id).map((l) => [l.source_layer, l]));
  const shown = caps.filter((c) => !q || `${c.name} ${c.title ?? ""} ${c.abstract ?? ""}`.toLowerCase().includes(q.toLowerCase()));
  const byGroup = useMemo(() => {
    const m = new Map<string, Cap[]>();
    shown.forEach((c) => { const g = c.group || ""; m.set(g, [...(m.get(g) ?? []), c]); });
    return [...m.entries()];
  }, [shown]);
  const add = useMutation({
    mutationFn: (approve: boolean) => http.post<{ created: number[]; status: string }>(`/api/v1/geoservices/${s.id}/layers`, {
      layers: caps.filter((c) => sel.has(c.name)).map((c) => ({ name: c.name, title: c.title ?? c.name, is_basemap: basemap })),
      group_name: group || "Spoljni servisi", allowed_roles: roles, approve,
    }),
    onSuccess: (r) => {
      notifications.show({ color: "green", title: r.status === "odobren" ? t("Slojevi dodati na kartu") : t("Slojevi predloženi"),
        message: r.status === "odobren" ? `${r.created.length} ${t("slojeva je odmah vidljivo izabranim rolama.")}` : `${r.created.length} ${t("slojeva čeka odobrenje administratora.")}` });
      inv(); onClose();
    }, onError: errNote(t),
  });
  const toggle = (n: string) => setSel((p) => { const x = new Set(p); if (x.has(n)) x.delete(n); else x.add(n); return x; });
  return (
    <Drawer opened onClose={onClose} position="right" size={560} title={<div><Text fw={700}>{t("Slojevi servisa")}</Text><Text size="xs" c="dimmed">{s.name}</Text></div>}>
      <Stack gap="sm">
        {!caps.length ? (
          <Alert color="orange" icon={<IconInfoCircle />}>{manager ? t("Spisak slojeva još nije učitan — kliknite „Provjeri“ na kartici servisa.") : t("Spisak slojeva još nije učitan. Zamolite administratora da provjeri vezu.")}</Alert>
        ) : (
          <>
            <Group gap="xs">
              <TextInput style={{ flex: 1 }} placeholder={t("Pretraga po nazivu")} leftSection={<IconSearch size={15} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} />
              <Button size="xs" variant="subtle" onClick={() => setSel(new Set(shown.filter((c) => !existing.has(c.name)).map((c) => c.name)))}>{t("Označi sve")}</Button>
              {sel.size > 0 && <Button size="xs" variant="subtle" color="gray" onClick={() => setSel(new Set())}>{t("Poništi")}</Button>}
            </Group>
            <Paper withBorder radius="md">
              <ScrollArea.Autosize mah={360}>
                {byGroup.map(([g, cs]) => (
                  <Box key={g}>
                    {g && <Text size="xs" fw={700} c="dimmed" tt="uppercase" px="sm" pt="xs">{g}</Text>}
                    {cs.map((c) => {
                      const ex = existing.get(c.name);
                      return (
                        <Group key={c.name} px="sm" py={6} wrap="nowrap" align="flex-start" style={{ borderBottom: "1px solid var(--mantine-color-default-border)", cursor: "pointer" }}
                          onClick={() => toggle(c.name)}>
                          <Checkbox checked={sel.has(c.name)} onChange={() => toggle(c.name)} onClick={(e) => e.stopPropagation()} mt={2} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Group gap={6} wrap="nowrap"><Text size="sm" fw={600} lineClamp={1}>{c.title ?? c.name}</Text>
                              {ex && <Badge size="xs" variant="light" color={STATUS_META[ex.status]?.color}>{t("već dodat")} · {t(STATUS_META[ex.status]?.label ?? ex.status)}</Badge>}
                              {c.queryable && <Tooltip label={t("Podržava klik na objekat (GetFeatureInfo)")}><Badge size="xs" variant="outline" color="gray">info</Badge></Tooltip>}</Group>
                            <Text size="xs" c="dimmed" ff="monospace" lineClamp={1}>{c.name}</Text>
                            {c.abstract && <Text size="xs" c="dimmed" lineClamp={2}>{c.abstract}</Text>}
                          </div>
                        </Group>
                      );
                    })}
                  </Box>
                ))}
                {!shown.length && <Empty text="Nema slojeva za pretragu" />}
              </ScrollArea.Autosize>
            </Paper>
            <Autocomplete label={t("Grupa u legendi")} data={groups} value={group} onChange={setGroup} />
            <RolesSelect value={roles} onChange={setRoles} label={manager ? "Vidljivo rolama" : "Predložene role (konačno određuje administrator)"} />
            <Switch label={t("Dodaj kao podlogu (osnovnu kartu)")} checked={basemap} onChange={(e) => setBasemap(e.currentTarget.checked)} />
            <Group justify="space-between" mt="xs">
              <Text size="sm" c="dimmed">{t("Izabrano")}: <b>{sel.size}</b></Text>
              <Group gap="xs">
                {manager ? (
                  <>
                    <Button variant="default" disabled={!sel.size} loading={add.isPending && add.variables === false} onClick={() => add.mutate(false)}>{t("Dodaj kao predlog")}</Button>
                    <Button leftSection={<IconCheck size={16} />} disabled={!sel.size} loading={add.isPending && add.variables === true} onClick={() => add.mutate(true)}>{t("Dodaj i odobri")}</Button>
                  </>
                ) : (
                  <Button leftSection={<IconPlus size={16} />} disabled={!sel.size} loading={add.isPending} onClick={() => add.mutate(false)}>{t("Dodaj (predlog)")}</Button>
                )}
              </Group>
            </Group>
            {!manager && <Text size="xs" c="dimmed">{t("Predlog vidi administrator")} ({user.username} → {t("Na odobrenju")}).</Text>}
          </>
        )}
      </Stack>
    </Drawer>
  );
}

// ============================================================================ Uvoz fajla
function UploadTab({ groups, manager, onGoPending }: { groups: string[]; manager: boolean; onGoPending: () => void }) {
  const { t } = useT();
  const inv = useInvalidate();
  const [file, setFile] = useState<File | null>(null);
  const [v, setV] = useState({ name: "", group_name: "Uvezeni slojevi", srs: null as string | null, stroke: "#c8453a", fill: "rgba(200, 69, 58, 0.12)",
    label: "", roles: [] as string[], approve: false });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((s) => ({ ...s, [k]: val }));
  const [res, setRes] = useState<(UploadResult & { title: string }) | null>(null);
  const up = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("file", file!);
      if (v.name) fd.append("name", v.name);
      fd.append("group_name", v.group_name || "Uvezeni slojevi");
      if (v.srs) fd.append("srs", v.srs);
      fd.append("stroke", v.stroke);
      fd.append("fill", v.fill);
      if (v.label) fd.append("label", v.label);
      if (v.roles.length) fd.append("allowed_roles", v.roles.join(","));
      fd.append("approve", String(manager && v.approve));
      return http.form<UploadResult>("/api/v1/geodata/upload", fd);
    },
    onSuccess: (r) => { setRes({ ...r, title: v.name || file!.name.replace(/\.[^.]+$/, "") }); inv(); }, onError: errNote(t),
  });
  const onDrop = (files: File[]) => {
    const f = files[0];
    if (!f) return;
    const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
    if (!EXTS.includes(ext)) { notifications.show({ color: "red", message: `${t("Format nije podržan")}: ${ext}. ${t("Podržano")}: ${EXTS.join(", ")}` }); return; }
    setFile(f); setRes(null);
    if (!v.name) set("name", f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " "));
  };
  const reset = () => { setFile(null); setRes(null); setV((s) => ({ ...s, name: "", label: "" })); };

  if (res) {
    const def: LayerDef = { id: res.layer_id, code: `fajl_${res.dataset_id}`, title: res.title, group_name: v.group_name, kind: "geojson",
      url: `/api/v1/geodata/${res.dataset_id}/features`, geoserver_layer: null, style: null,
      options: { stroke: v.stroke, fill: v.fill, label: v.label || undefined, bbox: res.bbox }, min_zoom: null, max_zoom: null,
      visible_default: true, opacity: 1, sort_order: 10, is_basemap: false, description: null };
    return (
      <Card withBorder radius="lg" padding="lg">
        <Group justify="space-between" mb="md">
          <Group gap="sm"><ThemeIcon size={40} radius="xl" color="green" variant="light"><IconCircleCheck /></ThemeIcon>
            <div><Title order={4}>{t("Fajl je uvezen")}</Title><Text size="sm" c="dimmed">{res.title}</Text></div></Group>
          <Badge size="lg" variant="light" color={STATUS_META[res.status]?.color}>{t(STATUS_META[res.status]?.label ?? res.status)}</Badge>
        </Group>
        <Grid>
          <Grid.Col span={{ base: 12, md: 7 }}><PreviewMap def={def} bbox={res.bbox} height={320} /></Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Table fz="sm" verticalSpacing={6}>
              <Table.Tbody>
                <Table.Tr><Table.Td c="dimmed">{t("Objekata")}</Table.Td><Table.Td fw={700}>{res.features.toLocaleString("sr-Latn-BA")}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td c="dimmed">{t("Geometrija")}</Table.Td><Table.Td>{res.geometry_type ?? "—"}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td c="dimmed">{t("Koordinatni sistem izvora")}</Table.Td><Table.Td>{res.srs ?? "—"}</Table.Td></Table.Tr>
                <Table.Tr><Table.Td c="dimmed">{t("Grupa")}</Table.Td><Table.Td>{v.group_name}</Table.Td></Table.Tr>
              </Table.Tbody>
            </Table>
            {res.fields.length > 0 && (
              <>
                <Text size="xs" c="dimmed" mt="sm" mb={4}>{t("Atributi")} ({res.fields.length})</Text>
                <Group gap={4}>{res.fields.slice(0, 30).map((f) => <Badge key={f} size="xs" variant="outline" color="gray" tt="none">{f}</Badge>)}</Group>
              </>
            )}
            <Alert mt="md" color={res.status === "odobren" ? "green" : "orange"} variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">{res.status === "odobren"
                ? t("Sloj je odobren i odmah vidljiv izabranim rolama na karti.")
                : t("Sloj čeka odobrenje administratora — do tada ga ne vidi niko na karti.")}</Text>
            </Alert>
            <Group mt="md" gap="xs">
              <Button variant="default" leftSection={<IconUpload size={16} />} onClick={reset}>{t("Uvezi još jedan")}</Button>
              {res.status !== "odobren" && <Button variant="light" onClick={onGoPending}>{t("Na odobrenje")}</Button>}
            </Group>
          </Grid.Col>
        </Grid>
      </Card>
    );
  }

  return (
    <Grid gap="md">
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Dropzone onDrop={onDrop} multiple={false} maxSize={300 * 1024 * 1024} radius="lg" loading={up.isPending}
          onReject={(r) => notifications.show({ color: "red", message: r[0]?.errors[0]?.message ?? t("Fajl nije prihvaćen") })}>
          <Stack align="center" gap={6} py="xl" style={{ pointerEvents: "none" }}>
            <Dropzone.Accept><IconCloudUpload size={48} color="var(--mantine-color-agri-6)" /></Dropzone.Accept>
            <Dropzone.Reject><IconX size={48} color="var(--mantine-color-red-6)" /></Dropzone.Reject>
            <Dropzone.Idle>{file ? <IconFile size={48} color="var(--mantine-color-agri-6)" /> : <IconMap size={48} opacity={0.5} />}</Dropzone.Idle>
            {file ? (
              <><Text fw={700}>{file.name}</Text><Text size="xs" c="dimmed">{(file.size / 1024 / 1024).toFixed(2)} MB · {t("kliknite ili prevucite drugi fajl za zamjenu")}</Text></>
            ) : (
              <><Text fw={650}>{t("Prevucite GIS fajl ovdje ili kliknite za izbor")}</Text>
                <Text size="xs" c="dimmed" ta="center" maw={380}>{t("Shapefile (ZIP sa .shp, .dbf, .shx, .prj), KML/KMZ, GeoJSON, GeoPackage, GML, CSV (WKT ili X/Y), FlatGeobuf, DXF, GPX — do 300 MB")}</Text></>
            )}
          </Stack>
        </Dropzone>
        <Paper withBorder radius="lg" p="sm" mt="md" bg="var(--mantine-color-default-hover)">
          <Text size="sm" fw={600} mb={4}>{t("Savjeti")}</Text>
          <Text size="xs" c="dimmed">• {t("Shapefile mora biti u ZIP-u zajedno sa .prj fajlom (koordinatni sistem).")}</Text>
          <Text size="xs" c="dimmed">• {t("Ako fajl nema .prj, izaberite koordinatni sistem — za katastar RS najčešće EPSG:8678 ili EPSG:3908.")}</Text>
          <Text size="xs" c="dimmed">• {t("CSV treba kolonu WKT ili kolone X i Y.")}</Text>
        </Paper>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Paper withBorder radius="lg" p="md">
          <Stack gap="sm">
            <TextInput label={t("Naziv sloja")} placeholder={t("npr. Zaštićena područja")} value={v.name} onChange={(e) => set("name", e.currentTarget.value)} />
            <Autocomplete label={t("Grupa u legendi")} data={groups} value={v.group_name} onChange={(x) => set("group_name", x)} />
            <Select label={t("Koordinatni sistem")} placeholder={t("Automatski (iz .prj ili fajla)")} data={SRS_OPTIONS} value={v.srs}
              onChange={(x) => set("srs", x)} clearable description={t("Popunite samo ako fajl nema podatak o koordinatnom sistemu.")} />
            <SimpleGrid cols={2} spacing="xs">
              <ColorInput label={t("Boja linije")} format="hex" value={v.stroke} onChange={(x) => set("stroke", x)} />
              <ColorInput label={t("Boja ispune")} format="rgba" value={v.fill} onChange={(x) => set("fill", x)} />
            </SimpleGrid>
            <TextInput label={t("Natpis (naziv atributa)")} placeholder={t("npr. NAZIV — može i kasnije")} value={v.label} onChange={(e) => set("label", e.currentTarget.value)} />
            <RolesSelect value={v.roles} onChange={(x) => set("roles", x)} label={manager ? "Vidljivo rolama" : "Predložene role (konačno određuje administrator)"} />
            {manager && <Switch label={t("Odmah odobri (bez koraka odobravanja)")} checked={v.approve} onChange={(e) => set("approve", e.currentTarget.checked)} />}
            <Button mt="xs" leftSection={<IconUpload size={16} />} disabled={!file} loading={up.isPending} onClick={() => up.mutate()}>
              {manager && v.approve ? t("Uvezi i objavi") : t("Uvezi (predlog)")}
            </Button>
          </Stack>
        </Paper>
      </Grid.Col>
    </Grid>
  );
}

